import React, { useState } from 'react';
import { Trash2, Eye, EyeOff } from 'lucide-react';
import type { User } from '../../schemas/validation';

interface DeleteUserModalProps {
  user: User;
  isSubmitting: boolean;
  onConfirm: (password: string) => Promise<void>;
  onCancel: () => void;
}

export const DeleteUserModal: React.FC<DeleteUserModalProps> = ({
  user,
  isSubmitting,
  onConfirm,
  onCancel,
}) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) {
      setError('Please enter your password');
      return;
    }
    setError('');
    try {
      await onConfirm(password);
    } catch (err: any) {
      setError(err.message || 'Failed to delete user');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center mb-4">
          <Trash2 className="w-6 h-6 text-red-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Delete User</h3>
        </div>

        <p className="text-gray-600 mb-4">
          Are you sure you want to delete <strong>{user.name}</strong>? This action cannot be
          undone.
        </p>

        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-4">
          <p className="text-sm text-amber-800">
            <strong>Security verification:</strong> Please enter your password to confirm this
            action.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label
              htmlFor="delete-password"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Your Password
            </label>
            <div className="relative">
              <input
                id="delete-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => {
                  setPassword(e.target.value);
                  setError('');
                }}
                disabled={isSubmitting}
                className="input-field pr-10"
                placeholder="Enter your password"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
          </div>

          <div className="flex space-x-4">
            <button
              type="submit"
              disabled={isSubmitting || !password.trim()}
              className="btn-danger flex-1"
            >
              {isSubmitting ? 'Deleting...' : 'Delete User'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              className="btn-secondary flex-1"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
